import { Box, Stack } from "@mui/material";
import { Custom, Icon } from "components";
import { useApp, useTheme } from "contexts";
import { Combines } from "helpers";
import { EnumTheme } from "utils/enums";

type Props = {
  key?: number | string;
  item: string;
  count: number;
};

export const Technology = ({ item, count }: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();
  const isDark = theme.id === EnumTheme.Dark;

  // Segmented experience gauge (max 8 segments)
  const totalPips = 5;
  const filledPips = Math.min(Math.max(Math.round(count / 1.5), 1), totalPips);

  return (
    <Box
      sx={{
        p: theme.spacing.sm,
        backgroundColor: theme.color.background.accent,
        border: `1px solid ${theme.color.border.color}`,
        borderRadius: theme.border.square,
        boxShadow: `2px 2px 0px 0px ${theme.color.border.color}`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translate(-2px, -2px)",
          boxShadow: `3px 3px 0px 0px ${isDark ? "#000000" : "#191b22"}`,
        },
        "&:active": {
          transform: "translate(2px, 2px)",
          boxShadow: "none",
        },
      }}
    >
      <Box>
        {/* Module Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: theme.spacing.xs,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.color.theme.color,
                border: `1px solid ${theme.color.border.color}`,
                borderRadius: theme.border.square,
              }}
            >
              <Icon.Svg size={18} name={item} />
            </Box>
            <Custom.Typography
              weight={theme.font.bold}
              sx={{
                fontSize: theme.font.sm,
                letterSpacing: "-0.01em",
                textTransform: "lowercase",
              }}
            >
              {item}
            </Custom.Typography>
          </Stack>

          {/* Micro Status Chip */}
          <Box
            sx={{
              fontFamily: '"Space Mono", monospace',
              fontSize: theme.font.xxxs,
              px: 0.6,
              py: 0.2,
              backgroundColor: theme.color.theme.color,
              border: `1px solid ${theme.color.border.color}`,
              color: theme.color.primary.color,
              fontWeight: theme.font.bold,
            }}
          >
            {count}Y
          </Box>
        </Box>
      </Box>

      {/* Tactile Gauge Bar */}
      <Box sx={{ mt: theme.spacing.xs }}>
        <Box
          sx={{
            display: "flex",
            gap: "3px",
            height: 4,
            width: "100%",
            mb: 0.5,
          }}
        >
          {Array.from({ length: totalPips }).map((_, pipIdx) => {
            const isFilled = pipIdx < filledPips;
            return (
              <Box
                key={pipIdx}
                sx={{
                  flex: 1,
                  height: "100%",
                  backgroundColor: isFilled
                    ? theme.color.primary.color
                    : theme.color.theme.color,
                  border: `1px solid ${
                    isFilled
                      ? theme.color.primary.color
                      : theme.color.border.color
                  }`,
                }}
              />
            );
          })}
        </Box>

        <Box
          sx={{
            fontFamily: '"Space Mono", monospace',
            fontSize: theme.font.xxxs,
            fontWeight: theme.font.medium,
            color: theme.color.font.accent,
            letterSpacing: "0.04em",
          }}
        >
          {Combines.interpolate(t.message.years_of_experience, {
            count: count,
          })}
        </Box>
      </Box>
    </Box>
  );
};